import React, { useEffect, useRef } from 'react';
import { jc } from 'utils';
import { IconButton, ArrowIcon } from 'fronton-react';
import { PlayIcon } from '@fronton/icons-react';
import { useTranslation } from 'react-i18next';
import { ThumbImage } from './thumb-image';
import { ThumbVideo } from './thumb-video';
import { MediaList } from '../types';
import {
  THUMB_COUNT,
  THUMB_COUNT_VIDEO,
  THUMB_SIZE,
  arrow,
  arrowBottom,
  arrowTop,
  container,
  listWrapper,
  playVideo,
  playVideoIcon,
  playVideoWrapper,
  root,
  thumbImageOutline,
} from './styles';

type Props = {
  active: number;
  data: MediaList;
  onClick?: (mediaType: string, imgSrc: string) => void;
  setActive: (index: number) => void;
};

function getIndex(index: number, length: number) {
  if (index >= length) {
    return index - length;
  }

  return index;
}

function ThumbList({ active, data, onClick, setActive }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const length = data.length;
  const list = [...data, ...data.slice(0, 3)];
  const firstVideo = data.findIndex(({ type }) => type === 'video');
  const thumbCount = firstVideo ? THUMB_COUNT_VIDEO : THUMB_COUNT;
  const height = length < thumbCount ? length * THUMB_SIZE : THUMB_SIZE * thumbCount;
  const isArrowTopVisible = length > thumbCount && active > 0;
  const isArrowBottomVisible = length > thumbCount && active !== length - 1;

  useEffect(() => {
    if (ref?.current) {
      const transition = THUMB_SIZE * active;
      ref.current.style.transform = `translateY(-${transition}px)`;
    }
  }, [active]);

  function handleClick(type: string, src: string, index: number) {
    updateActive(index);
    onClick?.(type, src);
  }

  function updateActive(index: number) {
    index = getIndex(index, length);
    setActive(index);
  }

  function goFirstVideo() {
    setActive(firstVideo);
    onClick?.(data[firstVideo].type, data[firstVideo].media.url || '');
  }

  function goNext() {
    updateActive(active + 1);
  }

  function goPrevious() {
    updateActive(active - 1);
  }

  return (
    <div data-qa="thumb-list" className={root}>
      <div style={{ height: `${height}px` }} className={container}>
        <div ref={ref} className={listWrapper}>
          {list.map(({ media, type }, idx) => {
            if (type === 'image') {
              return <ThumbImage src={media.url ?? ''} key={idx} index={idx} onClick={handleClick} />;
            }
            if (type === 'video') {
              return <ThumbVideo src={media.url ?? ''} key={idx} index={idx} onClick={handleClick} />;
            }
          })}
        </div>
        <div className={thumbImageOutline} />
        {firstVideo > -1 && (
          <div className={playVideoWrapper} onClick={goFirstVideo}>
            <div className={playVideo}>
              <PlayIcon
                className={playVideoIcon}
                type="fill"
                color="control-invert"
                size="s"
                data-qa="play-video"
              />
            </div>
          </div>
        )}
        {isArrowTopVisible && (
          <IconButton
            className={jc(arrow, arrowTop)}
            data-qa="thumb-list-arrow-top"
            variant="primary"
            label={t('media-gallery.thumbs-list.up')}
            size="m"
            onClick={goPrevious}
          >
            <ArrowIcon direction="top" />
          </IconButton>
        )}
        {isArrowBottomVisible && (
          <IconButton
            className={jc(arrow, arrowBottom)}
            data-qa="thumb-list-arrow-bottom"
            variant="primary"
            label={t('media-gallery.thumbs-list.down')}
            size="m"
            onClick={goNext}
          >
            <ArrowIcon direction="bottom" />
          </IconButton>
        )}
      </div>
    </div>
  );
}

export { ThumbList };
